export type Cents = number;
export type MoneyTotal = {
  currency: string;
  totalCents: Cents;
};
export type DollarRoundingMode =
  | "none"
  | "nearest_dollar"
  | "up_to_dollar"
  | "down_to_dollar";

const assertCents = (value: number): Cents => {
  if (!Number.isSafeInteger(value)) {
    throw new Error("Currency cents must be a safe integer");
  }
  return value;
};

export const centsFromApi = (value: unknown, fallback: Cents = 0): Cents => {
  if (value === null || value === undefined) return assertCents(fallback);
  if (typeof value !== "number") throw new Error("Currency API value must be a number of cents");
  return assertCents(value);
};

export const optionalCentsFromApi = (value: unknown): Cents | undefined =>
  value === null || value === undefined ? undefined : centsFromApi(value);

export const centsOrZero = (value?: Cents | null): Cents => value ?? 0;

const decimalRatio = (value: number | string) => {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(String(value));
  if (!match) throw new Error("Expected a non-negative decimal");
  const scale = BigInt(`1${"0".repeat((match[2] || "").length)}`);
  return {
    numerator: BigInt(match[1]) * scale + BigInt(match[2] || "0"),
    denominator: scale,
  };
};

const divideHalfUp = (numerator: bigint, denominator: bigint) => {
  if (denominator <= 0) throw new Error("Currency divisor must be positive");
  return numerator < 0
    ? -((-numerator + denominator / BigInt(2)) / denominator)
    : (numerator + denominator / BigInt(2)) / denominator;
};

export const addCents = (...values: Cents[]): Cents =>
  assertCents(values.reduce((total, value) => total + assertCents(value), 0));

export const subtractCents = (value: Cents, ...subtrahends: Cents[]): Cents =>
  assertCents(subtrahends.reduce((total, entry) => total - assertCents(entry), assertCents(value)));

const divideCents = (value: Cents, divisor: number): Cents => {
  if (!Number.isSafeInteger(divisor) || divisor <= 0) {
    throw new Error("Currency divisor must be a positive safe integer");
  }
  return assertCents(Number(divideHalfUp(
    BigInt(assertCents(value)),
    BigInt(divisor),
  )));
};

/** The single rounding boundary for averages of currency values. */
export const averageCents = (values: readonly Cents[]): Cents =>
  values.length
    ? divideCents(
        values.reduce((total, value) => addCents(total, value), 0),
        values.length,
      )
    : 0;

export const netRevenueCents = (
  grossCents: Cents,
  refundsCents: Cents,
  commissionCents: Cents,
): Cents => subtractCents(grossCents, refundsCents, commissionCents);

export const percentageOfCents = (amountCents: Cents, percent: number | string): Cents => {
  const ratio = decimalRatio(percent);
  const raw = BigInt(assertCents(amountCents)) * ratio.numerator;
  return assertCents(Number(divideHalfUp(raw, BigInt(100) * ratio.denominator)));
};

const multiplyIntegerByDecimal = (
  amount: number,
  multiplier: number | string,
): number => {
  const ratio = decimalRatio(multiplier);
  return assertCents(Number(divideHalfUp(
    BigInt(assertCents(amount)) * ratio.numerator,
    ratio.denominator,
  )));
};

export const centsToRateUnits = (
  amountCents: Cents,
  unitsPerDollar: number | string,
): number => {
  const scaled = multiplyIntegerByDecimal(amountCents, unitsPerDollar);
  return Number(divideHalfUp(BigInt(scaled), BigInt(100)));
};

export const rateUnitsToCents = (
  units: number | string,
  unitsPerDollar: number | string,
): Cents => {
  const top = decimalRatio(units);
  const bottom = decimalRatio(unitsPerDollar);
  return assertCents(Number(divideHalfUp(
    top.numerator * bottom.denominator * BigInt(100),
    top.denominator * bottom.numerator,
  )));
};

export const roundCentsToDollars = (
  valueCents: Cents,
  mode: DollarRoundingMode = "none",
): Cents => {
  const value = assertCents(valueCents);
  if (mode === "none") return value;
  const quotient = Math.trunc(value / 100);
  const remainder = value % 100;
  if (remainder === 0) return value;
  if (mode === "up_to_dollar") return assertCents((quotient + (value > 0 ? 1 : 0)) * 100);
  if (mode === "down_to_dollar") return assertCents((quotient - (value < 0 ? 1 : 0)) * 100);
  return assertCents((quotient + (Math.abs(remainder) >= 50 ? Math.sign(value) : 0)) * 100);
};

/** Parse a user/provider decimal string with the shared ROUND_HALF_UP policy. */
export const decimalStringToCents = (input: string): Cents => {
  const text = input.trim();
  const match = /^([+-]?)(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) throw new Error("Enter a valid currency amount");
  const sign = match[1] === "-" ? -1 : 1;
  const whole = Number(match[2]);
  if (!Number.isSafeInteger(whole)) throw new Error("Currency amount is too large");
  const fraction = (match[3] || "").padEnd(3, "0");
  let cents = whole * 100 + Number(fraction.slice(0, 2));
  if (Number(fraction[2] || "0") >= 5) cents += 1;
  return assertCents(sign * cents);
};

/** Parses an in-progress currency input without throwing while the user edits. */
export const tryDecimalStringToCents = (input: string): Cents | undefined => {
  const text = input.trim();
  if (!text || text === ".") return undefined;
  if (!/^\d+(?:\.\d*)?$/.test(text)) return undefined;
  return decimalStringToCents(text);
};

export const centsToInput = (value: Cents): string => {
  const cents = assertCents(value);
  const sign = cents < 0 ? "-" : "";
  const absolute = Math.abs(cents);
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
};

const currencyFormatters = new Map<string, Intl.NumberFormat>();

export const formatCents = (
  value: Cents,
  options?: Intl.NumberFormatOptions | number
): string => {
  const normalizedOptions = typeof options === "number"
    ? { minimumFractionDigits: options, maximumFractionDigits: options }
    : options;
  const formatOptions = {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...normalizedOptions,
  } satisfies Intl.NumberFormatOptions;
  const key = JSON.stringify(formatOptions);
  let formatter = currencyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat("en-US", formatOptions);
    currencyFormatters.set(key, formatter);
  }
  return formatter.format(assertCents(value) / 100);
};

export const formatMoneyTotals = (
  totals?: readonly MoneyTotal[] | null,
): string => {
  if (!totals?.length) return formatCents(0);
  const nonZero = totals.filter(({ totalCents }) => totalCents !== 0);
  return (nonZero.length ? nonZero : [totals[0]])
    .map(({ currency, totalCents }) =>
      formatCents(totalCents, { currency }),
    )
    .join(" + ");
};

export const formatCurrencyCodeWithSymbol = (currency: string): string => {
  const code = currency.trim().toUpperCase();
  if (!code) return "—";
  try {
    const symbol = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      currencyDisplay: "symbol",
    }).formatToParts(0).find((part) => part.type === "currency")?.value;
    return symbol ? `${code} (${symbol})` : code;
  } catch {
    return code;
  }
};

export const moneyTotal = (
  currency: string,
  totalCents: Cents,
): MoneyTotal => ({ currency, totalCents: assertCents(totalCents) });

export const mergeMoneyTotals = (
  groups: ReadonlyArray<readonly MoneyTotal[] | undefined>,
): MoneyTotal[] => {
  const totals = new Map<string, Cents>();
  groups.forEach((group) =>
    group?.forEach(({ currency, totalCents }) =>
      totals.set(currency, addCents(totals.get(currency) || 0, totalCents)),
    ),
  );
  return Array.from(totals, ([currency, totalCents]) => ({
    currency,
    totalCents,
  })).sort((left, right) => left.currency.localeCompare(right.currency));
};
